import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent, MatCardHeader, MatCardSubtitle, MatCardTitle } from '@angular/material/card';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';
import { AnalyticsService, LoggingRecord } from '../../_services/analytics.service';
import { UnivPrefsService } from '../../_services/univ-prefs.service';
import { DateAgoPipe } from '../../_pipes/date-ago.pipe';
import {
  ANALYTICS_SAMPLE_SIZE,
  ActivityAudience,
  AnalyticsSummary,
  CollapsedActivity,
  collapseActivity,
  filterCollapsedActivity,
  summarizeSchoolActivity,
} from './analytics.utils';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css'],
  imports: [
    MatButton,
    MatCard,
    MatCardHeader,
    MatCardTitle,
    MatCardSubtitle,
    MatCardContent,
    DatePipe,
    DecimalPipe,
    DateAgoPipe,
  ]
})
export class AnalyticsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly univPrefs = inject(UnivPrefsService);

  protected readonly sampleSize = ANALYTICS_SAMPLE_SIZE;

  schoolId = '';
  recentRecords: LoggingRecord[] = [];
  collapsedActivity: CollapsedActivity[] = [];
  summary: AnalyticsSummary = {
    lastSchoolVisit: null,
    schoolUserCount: 0,
    topScreen: null,
    screenCounts: [],
  };
  audience: ActivityAudience = 'school';
  sampleCapped = false;
  isLoading = true;
  isExporting = false;
  errorMessage = '';

  private schoolEmails = new Set<string>();

  ngOnInit() {
    this.schoolId = this.route.snapshot.parent?.paramMap.get('schoolId') ?? '';
    this.titleService.setTitle('Analytics | NPC Admin');
    void this.load();
  }

  get visibleActivity(): CollapsedActivity[] {
    return filterCollapsedActivity(this.collapsedActivity, this.audience);
  }

  get sampleOldest(): Date | null {
    const last = this.recentRecords[this.recentRecords.length - 1];
    return last?.time?.toDate() ?? null;
  }

  setAudience(audience: ActivityAudience) {
    this.audience = audience;
  }

  activityRowId(index: number): string {
    return `analytics-activity-${index}`;
  }

  async exportCsv() {
    if (!this.schoolId || this.isExporting) {
      return;
    }

    this.isExporting = true;
    this.errorMessage = '';

    try {
      const { csv, filename } = await this.analyticsService.exportLoggingCsv(
        this.schoolId,
        new Date('2026-03-01T00:00:00')
      );

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');

      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Unable to export analytics records', error);
      this.errorMessage = 'Unable to export analytics records right now.';
    } finally {
      this.isExporting = false;
    }
  }

  private async load() {
    if (!this.schoolId) {
      this.isLoading = false;
      this.errorMessage = 'No school selected.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    try {
      await Promise.all([this.loadSchoolEmails(), this.loadSummary()]);
      this.rebuildDerived();
    } catch (error) {
      console.error('Unable to load analytics records', error);
      this.errorMessage = 'Unable to load analytics records right now.';
    } finally {
      this.isLoading = false;
    }
  }

  private async loadSchoolEmails() {
    if (!this.univPrefs.activeUsers) {
      this.schoolEmails = new Set();
      return;
    }

    const users = await firstValueFrom(this.univPrefs.activeUsers.pipe(take(1)));
    this.schoolEmails = new Set(
      (users ?? [])
        .map(user => (user.email || '').trim().toLowerCase())
        .filter(Boolean)
    );
  }

  private async loadSummary() {
    const recent = await this.analyticsService.getRecentLogging(this.schoolId, this.sampleSize);
    this.recentRecords = recent.records;
    this.sampleCapped = recent.capped;
  }

  private rebuildDerived() {
    const activityRecords = this.recentRecords.map(record => ({
      email: record.email,
      path: record.path,
      at: record.time?.toDate() ?? null,
    }));

    this.collapsedActivity = collapseActivity(activityRecords, this.schoolEmails);
    this.summary = summarizeSchoolActivity(activityRecords, this.schoolEmails);
  }
}
